import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GiftIcon,
  MinusIcon,
  PlusIcon,
  ShoppingCartIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { getLocalStorageItem, successToast, errorToast } from '../../../utils/helper';
import { CompanyApi } from '../../../api/companyApi';
import { PER_PAGE_12 } from '../../../utils/constants';
import Pagination from '../../../component/common/Pagination/Pagination';
import SelectMenu from '../../../component/common/SelectMenu';
import cartService from './cartService';
import ProductDetail from './ProductDetail';

const EGiftCard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE_12[0]);
  const [selectedDenominations, setSelectedDenominations] = useState({});
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [cartUpdateTrigger, setCartUpdateTrigger] = useState(0);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;
  const slug = userData?.slug || 'b2b';

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await CompanyApi.getVoucherlineProducts(
        'e_gifts',
        currentPage,
        selectedPerPage.value
      );

      if (response?.data?.meta?.code === 1 && response?.data?.data) {
        const data = (response.data.data || []).map((p) => ({
          ...p,
          _id: p._id || p.productCode
        }));
        setProducts(data);
        setTotalCount(response.data.meta.totalCount || 0);
      } else {
        setProducts([]);
        setTotalCount(0);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedPerPage]);

  useEffect(() => {
    const count = cartService.getUniqueProductsCount();
    setCartItemsCount(count);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const getCartItemKey = (productId, denomination) => `${productId}_${denomination}`;

  const getCartQuantity = (productId, denomination) => {
    const item = cartService.getCartItem(getCartItemKey(productId, denomination));
    return item ? item.quantity : 0;
  };

  const isProductInCart = (productId, denomination) =>
    cartService.isInCart(productId, denomination);

  const getSelectedDenomination = (product) => {
    if (!product.denominations?.length) return null;
    return selectedDenominations[product._id] || product.denominations[0];
  };

  const handleDenominationChange = (productId, denomination) => {
    setSelectedDenominations((prev) => ({ ...prev, [productId]: denomination }));
    setCartUpdateTrigger((prev) => prev + 1);
  };

  const handleAddToCart = (product) => {
    const denomination = getSelectedDenomination(product);
    if (!denomination) {
      errorToast('Please select a denomination');
      return;
    }
    const result = cartService.addToCart(product, 1, denomination);
    if (result.success) {
      setCartItemsCount(result.uniqueProducts);
      setCartUpdateTrigger((prev) => prev + 1);
      successToast(`Added to cart: ${product.currencyCode} ${denomination}`);
    } else {
      errorToast('Failed to add item to cart. Please try again.');
    }
  };

  const handleIncrement = (product, denomination) => {
    const key = getCartItemKey(product._id, denomination);
    const result = cartService.updateCartItemQuantity(
      key,
      getCartQuantity(product._id, denomination) + 1
    );
    if (result.success) {
      setCartItemsCount(result.uniqueProducts);
      setCartUpdateTrigger((prev) => prev + 1);
      successToast('Quantity updated');
    } else {
      errorToast('Failed to update quantity');
    }
  };

  const handleDecrement = (product, denomination) => {
    const key = getCartItemKey(product._id, denomination);
    const currentQty = getCartQuantity(product._id, denomination);
    const result =
      currentQty <= 1
        ? cartService.removeFromCart(key)
        : cartService.updateCartItemQuantity(key, currentQty - 1);

    if (result.success) {
      setCartItemsCount(result.uniqueProducts);
      setCartUpdateTrigger((prev) => prev + 1);
      successToast(currentQty <= 1 ? 'Removed from cart' : 'Quantity updated');
    } else {
      errorToast('Failed to update cart');
    }
  };

  const handleQuantityInputChange = (product, denomination, value) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1) {
      const key = getCartItemKey(product._id, denomination);
      const result = cartService.updateCartItemQuantity(key, numValue);
      if (result.success) {
        setCartItemsCount(result.uniqueProducts);
        setCartUpdateTrigger((prev) => prev + 1);
        successToast('Quantity updated');
      } else {
        errorToast('Failed to update quantity');
      }
    }
  };

  return (
    <div className="mt-6">
      {/* Action buttons */}
      <div className="flex justify-between items-center gap-3 mb-6">
        <div className="w-[80px]">
          <SelectMenu
            menuList={PER_PAGE_12}
            showLabel={false}
            defaultSelected={selectedPerPage}
            setSelectedMenu={(opt) => {
              setSelectedPerPage(opt);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/${slug}/marketplace/orders`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            View Orders
          </button>
          <button
            onClick={() => navigate(`/${slug}/marketplace/cart`)}
            className="inline-flex mr-2 items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 relative"
          >
            <ShoppingCartIcon className="h-5 w-5 mr-2" />
            Cart
            {cartItemsCount > 0 && (
              <span className="absolute -top-4 -right-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                {cartItemsCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor shadow rounded-lg">
        <div className="px-4 py-8 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <svg
                  className="animate-spin h-12 w-12 mx-auto text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading items...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Error loading items
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {products.map((product) => (
                  <div
                    key={product._id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-200 dark:border-shoorah-darkBorder flex flex-col"
                  >
                    <div
                      className="relative h-48 bg-gray-100 dark:bg-gray-700 cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setIsProductModalOpen(true);
                      }}
                    >
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.productName}
                          className="w-full h-full object-contain p-2 hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ display: product.imageUrl ? 'none' : 'flex' }}
                      >
                        <GiftIcon className="h-16 w-16 text-gray-400" />
                      </div>
                      {product.customerDiscountPercentage > 0 && (
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            {product.customerDiscountPercentage}% OFF
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-4 flex flex-col flex-grow">
                      {product.brandName && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
                          {product.brandName}
                        </p>
                      )}
                      <h3
                        className="text-sm font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                        onClick={() => {
                          setSelectedProduct(product);
                          setIsProductModalOpen(true);
                        }}
                      >
                        {product.productName}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Code: {product.productCode}
                      </p>

                      {product.denominations?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1.5 font-medium">
                            Select value:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {product.denominations.map((value, index) => {
                              const isSelected = getSelectedDenomination(product) === value;
                              return (
                                <button
                                  key={index}
                                  onClick={() => handleDenominationChange(product._id, value)}
                                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
                                    isSelected
                                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-2 border-blue-500 dark:border-blue-400'
                                      : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-700 hover:border-blue-300 dark:hover:border-blue-500'
                                  }`}
                                >
                                  {product.currencyCode} {value}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="mt-auto">
                        {(() => {
                          const denomination = getSelectedDenomination(product);
                          const inCart = isProductInCart(product._id, denomination);
                          const cartQty = getCartQuantity(product._id, denomination);

                          if (inCart) {
                            return (
                              <div className="flex items-center w-[50%] border border-gray-300 dark:border-gray-600 rounded-lg">
                                <button
                                  onClick={() => handleDecrement(product, denomination)}
                                  className="px-2 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-lg transition-colors duration-200"
                                >
                                  <MinusIcon className="h-4 w-4" />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={cartQty}
                                  onChange={(e) =>
                                    handleQuantityInputChange(product, denomination, e.target.value)
                                  }
                                  className="w-12 text-center border-x border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none py-2"
                                />
                                <button
                                  onClick={() => handleIncrement(product, denomination)}
                                  className="px-2 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-lg transition-colors duration-200"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          }
                          return (
                            <button
                              onClick={() => handleAddToCart(product)}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                              <ShoppingCartIcon className="h-4 w-4" />
                              Add to Cart
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalCount > selectedPerPage.value && (
                <Pagination
                  currentPage={currentPage}
                  totalCount={totalCount}
                  pageSize={selectedPerPage.value}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No items available
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                No items found in this category yet.
              </p>
            </div>
          )}
        </div>
      </div>

      <ProductDetail
        isOpen={isProductModalOpen}
        product={selectedProduct}
        onClose={() => {
          setIsProductModalOpen(false);
          setSelectedProduct(null);
        }}
        onCartUpdate={setCartItemsCount}
      />
    </div>
  );
};

export default EGiftCard;
